/** @format */
import { Pipe, PipeTransform } from '@angular/core';
import { EntityState } from '@datorama/akita';
import {
    AdmissionTarget,
    AssessmentItem,
    AttainedQualification,
    Attainment,
    CooperationNetwork,
    CourseUnit,
    CourseUnitRealisation,
    CurriculumPeriod,
    Education,
    EnrolmentRight,
    ExternalIssuer,
    GradeScale,
    Location,
    OpenUniversityProduct,
    OpenUniversityProductCode,
    Organisation,
    OtmId,
    PaymentCategory,
    PersonInfo,
    PrivatePerson,
    PrivatePersonBasicInfo,
    PublicPerson,
    Qualification,
    StudyEvent,
    Workflow,
} from 'common-typescript/types';
import { Observable } from 'rxjs';
import { AppErrorHandler } from '../error-handler/app-error-handler';
import { AdmissionTargetEntityService } from '../service/admission-target-entity.service';
import { AssessmentItemEntityService } from '../service/assessment-item-entity.service';
import { AttainedQualificationEntityService } from '../service/attained-qualification-entity.service';
import { AttainmentEntityService } from '../service/attainment-entity.service';
import { CooperationNetworkEntityService } from '../service/cooperation-network-entity.service';
import { CourseUnitEntityService } from '../service/course-unit-entity.service';
import { CourseUnitRealisationEntityService } from '../service/course-unit-realisation-entity.service';
import { CurriculumPeriodEntityService } from '../service/curriculum-period-entity.service';
import { EducationEntityService } from '../service/education-entity.service';
import { EnrolmentRightEntityService } from '../service/enrolment-right-entity.service';
import { EntityService } from '../service/entity.service';
import { ExternalIssuerEntityService } from '../service/external-issuer-entity.service';
import { GradeScaleEntityService } from '../service/grade-scale-entity.service';
import { LocationEntityService } from '../service/location-entity.service';
import { OpenUniversityProductCodeEntityService } from '../service/open-university-product-code-entity.service';
import { OpenUniversityProductEntityService } from '../service/open-university-product-entity.service';
import { OrganisationEntityService } from '../service/organisation-entity.service';
import { PaymentCategoryEntityService } from '../service/payment-category-entity.service';
import { PersonInfoEntityService } from '../service/person-info-entity.service';
import { PrivatePersonBasicInfoEntityService } from '../service/private-person-basic-info-entity.service';
import { PrivatePersonEntityService } from '../service/private-person-entity.service';
import { PublicPersonEntityService } from '../service/public-person-entity.service';
import { QualificationEntityService } from '../service/qualification-entity.service';
import { StudyEventEntityService } from '../service/study-event-entity.service';
import { WorkflowEntityService } from '../service/workflow-entity.service';

export abstract class EntityPipe<T> implements PipeTransform {
    protected constructor(
        private readonly appErrorHandler: AppErrorHandler,
        private readonly entityService: EntityService<EntityState<T, OtmId>>,
    ) {}

    /**
     * Return null on falsy id.
     * Return observable of null when error is caught. This simulates the pattern where service calls are handled with default promise
     * handler and no value is assigned to variable in component.
     */
    transform(id: OtmId): Observable<T> {
        if (!id) {
            return null;
        }
        return this.entityService.getById(id).pipe(this.appErrorHandler.defaultErrorHandler());
    }
}

@Pipe({
    name: 'admissionTarget',
    standalone: false,
})
export class AdmissionTargetPipe extends EntityPipe<AdmissionTarget> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: AdmissionTargetEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'assessmentItem',
    standalone: false,
})
export class AssessmentItemPipe extends EntityPipe<AssessmentItem> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: AssessmentItemEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'attainedQualification',
    standalone: false,
})
export class AttainedQualificationPipe extends EntityPipe<AttainedQualification> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: AttainedQualificationEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'attainment',
    standalone: false,
})
export class AttainmentPipe extends EntityPipe<Attainment> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: AttainmentEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'cooperationNetwork',
    standalone: false,
})
export class CooperationNetworkPipe extends EntityPipe<CooperationNetwork> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: CooperationNetworkEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'courseUnit',
    standalone: false,
})
export class CourseUnitPipe extends EntityPipe<CourseUnit> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: CourseUnitEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'courseUnitRealisation',
    standalone: false,
})
export class CourseUnitRealisationPipe extends EntityPipe<CourseUnitRealisation> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: CourseUnitRealisationEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'curriculumPeriod',
    standalone: false,
})
export class CurriculumPeriodPipe extends EntityPipe<CurriculumPeriod> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: CurriculumPeriodEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'enrolmentRight',
    standalone: false,
})
export class EnrolmentRightPipe extends EntityPipe<EnrolmentRight> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: EnrolmentRightEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'education',
    standalone: false,
})
export class EducationPipe extends EntityPipe<Education> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: EducationEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'openUniversityProduct',
    standalone: false,
})
export class OpenUniversityProductPipe extends EntityPipe<OpenUniversityProduct> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: OpenUniversityProductEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'openUniversityProductCode',
    standalone: false,
})
export class OpenUniversityProductCodePipe extends EntityPipe<OpenUniversityProductCode> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: OpenUniversityProductCodeEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'organisation',
    standalone: false,
})
export class OrganisationPipe extends EntityPipe<Organisation> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: OrganisationEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'paymentCategory',
    standalone: false,
})
export class PaymentCategoryPipe extends EntityPipe<PaymentCategory> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: PaymentCategoryEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'personInfo',
    standalone: false,
})
export class PersonInfoPipe extends EntityPipe<PersonInfo> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: PersonInfoEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'privatePerson',
    standalone: false,
})
export class PrivatePersonPipe extends EntityPipe<PrivatePerson> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: PrivatePersonEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'privatePersonBasicInfo',
    standalone: false,
})
export class PrivatePersonBasicInfoPipe extends EntityPipe<PrivatePersonBasicInfo> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: PrivatePersonBasicInfoEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'publicPerson',
    standalone: false,
})
export class PublicPersonPipe extends EntityPipe<PublicPerson> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: PublicPersonEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'qualification',
    standalone: false,
})
export class QualificationPipe extends EntityPipe<Qualification> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: QualificationEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'studyEvent',
    standalone: false,
})
export class StudyEventPipe extends EntityPipe<StudyEvent> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: StudyEventEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'gradeScale',
    standalone: false,
})
export class GradeScalePipe extends EntityPipe<GradeScale> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: GradeScaleEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'location',
    standalone: false,
})
export class LocationPipe extends EntityPipe<Location> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: LocationEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'workflow',
    standalone: false,
})
export class WorkflowPipe extends EntityPipe<Workflow> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: WorkflowEntityService) {
        super(appErrorHandler, service);
    }
}

@Pipe({
    name: 'externalIssuer',
    standalone: false,
})
export class ExternalIssuerPipe extends EntityPipe<ExternalIssuer> implements PipeTransform {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(appErrorHandler: AppErrorHandler, service: ExternalIssuerEntityService) {
        super(appErrorHandler, service);
    }
}
